/**
 * Structured Partner API reference for the developer portal.
 * Keep in sync with backend/openapi/v1.yaml and /v1 routes.
 */

export const API_REF_VERSION = 'v1';

/**
 * One example per transportation_type in the Activity form. transportation_data
 * is stored as JSON with string values; *_time fields use YYYY-MM-DDTHH:mm and
 * set start_datetime / end_datetime when those are omitted.
 */
const transportExample = (type, title, location, data, extra = {}) => ({
  labelKey: `developerPortal.apiRef.itineraryExamples.transportTypes.${type}`,
  body: {
    title,
    location,
    category: 'transport',
    transportation_type: type,
    transportation_data: data,
    ...extra,
  },
});

const TRANSPORT_EXAMPLES = [
  transportExample('flight', 'Naples → Milan flight', 'Naples Airport (NAP)', {
    legs: [{
      airline: 'AZ',
      airline_code: 'AZ',
      flight_number: '1265',
      departure_airport: 'NAP',
      arrival_airport: 'LIN',
      departure_time: '2026-09-10T08:15',
      arrival_time: '2026-09-10T09:40',
    }],
    confirmation_number: 'ABC123',
    seat: '12A',
    gate: 'B4',
    terminal: '1',
    baggage: '1 checked bag',
    status: 'On time',
  }, { cost: 186, cost_currency: 'EUR' }),
  transportExample('taxi', 'Taxi to hotel', 'Naples Airport', {
    pickup_location: 'Naples Airport arrivals',
    dropoff_location: 'Hotel Santa Caterina, Amalfi',
    pickup_time: '2026-09-10T10:00',
    driver_name: 'Marco',
    vehicle_type: 'Mercedes V-Class',
    estimated_cost: '140',
    actual_cost: '135',
    booking_reference: 'TX-5521',
  }, { end_datetime: '2026-09-10T11:15', cost: 135, cost_currency: 'EUR' }),
  transportExample('car_rental', 'Rental car', 'Sorrento', {
    pickup_location: 'Hertz Sorrento',
    dropoff_location: 'Hertz Naples Airport',
    pickup_time: '2026-09-13T09:00',
    return_time: '2026-09-16T18:00',
    rental_company: 'Hertz',
    car_type: 'Fiat 500 or similar',
    confirmation_number: 'HZ-90812',
    driver_license: 'Required: EU or international permit',
    insurance: 'Full coverage',
  }, { cost: 320, cost_currency: 'EUR' }),
  transportExample('uber', 'Uber to dinner', 'Positano', {
    pickup_location: 'Le Sirenuse, Positano',
    dropoff_location: 'Da Adolfo',
    pickup_time: '2026-09-12T19:30',
    uber_type: 'UberX',
    driver_name: 'Luca',
    estimated_cost: '25',
    actual_cost: '23',
    trip_id: 'UBR-3F9A21',
  }, { end_datetime: '2026-09-12T19:50' }),
  transportExample('lyft', 'Lyft to airport', 'San Francisco', {
    pickup_location: 'Hotel Nikko',
    dropoff_location: 'SFO Terminal 2',
    pickup_time: '2026-09-20T06:00',
    lyft_type: 'Lyft XL',
    driver_name: 'Dana',
    estimated_cost: '55',
    actual_cost: '52',
    trip_id: 'LYF-77K2',
  }, { end_datetime: '2026-09-20T06:40' }),
  transportExample('train', 'Frecciarossa Naples → Rome', 'Napoli Centrale', {
    departure_station: 'Napoli Centrale',
    arrival_station: 'Roma Termini',
    departure_time: '2026-09-16T14:10',
    arrival_time: '2026-09-16T15:20',
    train_number: 'FR 9542',
    car_number: '6',
    seat: '7A',
    confirmation_number: 'TRN-7731',
    class: 'Business',
  }, { cost: 64, cost_currency: 'EUR' }),
  transportExample('bus', 'SITA bus to Ravello', 'Amalfi bus terminal', {
    departure_station: 'Amalfi (Piazza Flavio Gioia)',
    arrival_station: 'Ravello',
    departure_time: '2026-09-11T17:30',
    arrival_time: '2026-09-11T18:00',
    bus_number: '5080',
    seat: '14',
    confirmation_number: 'SITA-221',
    operator: 'SITA Sud',
  }, { cost: 4, cost_currency: 'EUR' }),
  transportExample('subway', 'Metro to the Colosseum', 'Termini', {
    departure_station: 'Termini',
    arrival_station: 'Colosseo',
    departure_time: '2026-09-17T09:00',
    line: 'B',
    direction: 'Laurentina',
    fare: '1.50 EUR',
  }, { end_datetime: '2026-09-17T09:15' }),
  transportExample('ferry', 'Ferry Amalfi → Positano', 'Amalfi harbour', {
    departure_port: 'Amalfi',
    arrival_port: 'Positano',
    departure_time: '2026-09-12T09:30',
    arrival_time: '2026-09-12T10:05',
    ferry_company: 'Travelmar',
    vessel_name: 'Santa Lucia',
    confirmation_number: 'TM-44120',
    vehicle: 'None (foot passengers)',
  }, { cost: 40, cost_currency: 'EUR' }),
  transportExample('cruise', 'Mediterranean cruise', 'Port of Civitavecchia', {
    cruise_line: 'MSC Cruises',
    ship_name: 'MSC World Europa',
    departure_port: 'Civitavecchia',
    departure_time: '2026-09-18T17:00',
    cabin_number: '12045',
    confirmation_number: 'MSC-88120',
    duration: '7 nights',
    ports_of_call: 'Genoa, Marseille, Barcelona, Palermo',
  }, { end_datetime: '2026-09-25T08:00', cost: 2400, cost_currency: 'EUR' }),
  transportExample('walking', 'Walk to the Duomo', 'Amalfi', {
    start_location: 'Hotel Santa Caterina',
    end_location: 'Duomo di Amalfi',
    start_time: '2026-09-11T10:00',
    estimated_duration: '20 min',
    distance: '1.2 km',
    route: 'Along the seafront promenade',
  }, { end_datetime: '2026-09-11T10:20' }),
  transportExample('bicycle', 'E-bike along the coast', 'Sorrento', {
    start_location: 'Sorrento Bike Rental',
    end_location: 'Massa Lubrense',
    start_time: '2026-09-14T09:00',
    bike_rental: 'Sorrento Bike Rental (e-bike)',
    estimated_duration: '4 h',
    distance: '28 km',
    route: 'Coastal loop via Sant’Agata',
  }, { end_datetime: '2026-09-14T13:00', cost: 45, cost_currency: 'EUR' }),
  transportExample('motorcycle', 'Vespa day', 'Positano', {
    start_location: 'Positano Scooter Rental',
    end_location: 'Positano Scooter Rental',
    start_time: '2026-09-15T09:00',
    rental_company: 'Positano Scooter',
    motorcycle_type: 'Vespa 125',
    helmet_required: 'Yes',
  }, { end_datetime: '2026-09-15T17:00', cost: 70, cost_currency: 'EUR' }),
];

const MEMBER_ITINERARY_BODY_PARAMS = [
  { name: 'title', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.itineraryTitle' },
  { name: 'visibility', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.memberItineraryVisibility' },
  { name: 'subgroup_name', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.memberItinerarySubgroupName' },
  { name: 'participants', type: 'array', required: false, descriptionKey: 'developerPortal.apiRef.params.memberItineraryParticipants' },
  { name: 'location', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryLocation' },
  { name: 'google_map_link', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryGoogleMapLink' },
  { name: 'category', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCategory' },
  { name: 'transportation_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryTransportationType' },
  { name: 'transportation_data', type: 'object', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryTransportationData' },
  { name: 'start_datetime', type: 'datetime', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryStartDatetime' },
  { name: 'end_datetime', type: 'datetime', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryEndDatetime' },
  { name: 'day_number', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryDayNumber' },
  { name: 'start_time', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryStartTime' },
  { name: 'description', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryDescription' },
  { name: 'cost', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCost' },
  { name: 'cost_currency', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCostCurrency' },
];

const MEMBER_ITINERARY_RESPONSE_FIELDS = [
  { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.memberItinerary.id' },
  { name: 'visibility', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.memberItinerary.visibility' },
  { name: 'subgroup_name', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.memberItinerary.subgroupName' },
  { name: 'owner_user_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.memberItinerary.ownerUserId' },
  { name: 'participants', type: 'array', descriptionKey: 'developerPortal.apiRef.fields.memberItinerary.participants' },
];

const MEMBER_ITINERARY_SUBGROUP_RESPONSE = {
  id: 901,
  trip_id: 1001,
  name: 'Boat to Capri',
  title: 'Boat to Capri',
  location: 'Positano pier',
  google_map_link: null,
  category: 'transport',
  transportation_type: 'ferry',
  transportation_data: {
    departure_port: 'Positano',
    arrival_port: 'Capri',
    departure_time: '2026-09-13T09:00',
    arrival_time: '2026-09-13T09:45',
    ferry_company: 'NLG',
  },
  start_datetime: '2026-09-13 09:00:00',
  end_datetime: '2026-09-13 09:45:00',
  description: null,
  cost: 60,
  cost_currency: 'EUR',
  day_number: 4,
  visibility: 'subgroup',
  subgroup_name: 'Capri crew',
  owner_user_id: 42,
  participants: [
    { participant_key: 'user:43', user_id: 43, invitation_id: null, name: 'omar.khan', username: 'omar.khan', email: 'omar.khan@example.com', status: 'active' },
    { participant_key: 'invite:501', user_id: null, invitation_id: 501, name: 'lina.park@example.com', username: 'lina.park@example.com', email: 'lina.park@example.com', status: 'pending' },
  ],
  created_at: '2026-10-08T19:02:11.000Z',
  updated_at: '2026-10-08T19:02:11.000Z',
};

const CATALOG_FIELD = (name, type, key) => ({
  name, type, descriptionKey: `developerPortal.apiRef.fields.catalog.${key}`,
});

/**
 * Inventory and add-ons share one shape. Inventory also has capacity; add-ons
 * use their own categories.
 */
const CATALOG_DOCS = {
  inventory: {
    path: '/v1/inventory',
    ids: { list: 'list-inventory', create: 'create-inventory-item', get: 'get-inventory-item', update: 'update-inventory-item', archive: 'archive-inventory-item' },
    keys: { list: 'listInventory', create: 'createInventoryItem', get: 'getInventoryItem', update: 'updateInventoryItem', archive: 'archiveInventoryItem' },
    categoryParamKey: 'catalogCategoryInventory',
    sample: {
      id: 14,
      name: 'Riva Aquarama day charter',
      category: 'yacht',
      description: 'Classic wooden speedboat with skipper, Positano to Capri.',
      image_url: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1200',
      default_price: 1800,
      price_frequency: 'per_day',
      currency: 'EUR',
      notes: 'Book 48h ahead; weather dependent.',
      payment_link_url: 'https://buy.stripe.com/yacht',
      payment_link_label: 'Pay charter',
      active: true,
      external_id: 'yacht-riva-01',
      capacity: 8,
      created_at: '2026-10-08T19:10:00.000Z',
      updated_at: '2026-10-08T19:10:00.000Z',
    },
  },
  addon: {
    path: '/v1/addons',
    ids: { list: 'list-addons', create: 'create-addon', get: 'get-addon', update: 'update-addon', archive: 'archive-addon' },
    keys: { list: 'listAddons', create: 'createAddon', get: 'getAddon', update: 'updateAddon', archive: 'archiveAddon' },
    categoryParamKey: 'catalogCategoryAddon',
    sample: {
      id: 21,
      name: 'Airport fast track',
      category: 'transfer',
      description: 'Meet and greet at the gate, fast-track security and passport control.',
      image_url: null,
      default_price: 80,
      price_frequency: 'one_time',
      currency: 'EUR',
      notes: null,
      payment_link_url: null,
      payment_link_label: null,
      active: true,
      external_id: 'fast-track-nap',
      created_at: '2026-10-08T19:12:00.000Z',
      updated_at: '2026-10-08T19:12:00.000Z',
    },
  },
};

function catalogEndpoints(kind) {
  const doc = CATALOG_DOCS[kind];
  const itemPath = `${doc.path}/{itemId}`;
  const keyBase = (name) => `developerPortal.apiRef.endpoints.${doc.keys[name]}`;
  const hasCapacity = kind === 'inventory';
  const responseFields = [
    CATALOG_FIELD('id', 'integer', 'id'),
    CATALOG_FIELD('name', 'string', 'name'),
    CATALOG_FIELD('category', 'string', kind === 'inventory' ? 'categoryInventory' : 'categoryAddon'),
    CATALOG_FIELD('default_price', 'number', 'defaultPrice'),
    CATALOG_FIELD('price_frequency', 'string', 'priceFrequency'),
    CATALOG_FIELD('currency', 'string', 'currency'),
    ...(hasCapacity ? [CATALOG_FIELD('capacity', 'integer|null', 'capacity')] : []),
    CATALOG_FIELD('active', 'boolean', 'active'),
    CATALOG_FIELD('external_id', 'string|null', 'externalId'),
    CATALOG_FIELD('payment_link_url', 'string|null', 'paymentLinkUrl'),
    CATALOG_FIELD('payment_link_label', 'string|null', 'paymentLinkLabel'),
  ];
  const param = (name, type, key, required = false) => ({
    name, type, required, descriptionKey: `developerPortal.apiRef.params.${key}`,
  });
  const bodyParams = (forCreate) => [
    param('name', 'string', 'catalogName', forCreate),
    param('default_price', 'number', 'catalogDefaultPrice', forCreate),
    param('category', 'string', doc.categoryParamKey),
    param('price_frequency', 'string', 'catalogPriceFrequency'),
    param('currency', 'string', 'catalogCurrency'),
    param('description', 'string', 'catalogDescription'),
    param('image_url', 'string', 'catalogImageUrl'),
    param('notes', 'string', 'catalogNotes'),
    param('payment_link_url', 'string', 'catalogPaymentLink'),
    param('payment_link_label', 'string', 'catalogPaymentLinkLabel'),
    ...(hasCapacity ? [param('capacity', 'integer', 'catalogCapacity')] : []),
    param('external_id', 'string', 'catalogExternalId'),
    ...(forCreate ? [] : [param('active', 'boolean', 'catalogActive')]),
  ];
  const itemIdParam = param('itemId', 'integer', 'catalogItemId', true);
  const { id, created_at: createdAt, updated_at: updatedAt, active, ...createBody } = doc.sample;
  const common = (name, method, path, scope) => ({
    id: doc.ids[name],
    method,
    path,
    scope,
    sandbox: true,
    titleKey: `${keyBase(name)}.title`,
    summaryKey: `${keyBase(name)}.summary`,
  });

  return {
    [doc.ids.list]: {
      ...common('list', 'GET', doc.path, 'packages:read'),
      queryParams: [
        param('category', 'string', 'catalogCategoryFilter'),
        param('include_inactive', 'boolean', 'catalogIncludeInactive'),
      ],
      responseFields,
      responseExample: { data: [doc.sample] },
    },
    [doc.ids.create]: {
      ...common('create', 'POST', doc.path, 'packages:write'),
      idempotent: true,
      bodyParams: bodyParams(true),
      requestExample: createBody,
      responseFields,
      responseExample: { data: doc.sample },
    },
    [doc.ids.get]: {
      ...common('get', 'GET', itemPath, 'packages:read'),
      pathParams: [itemIdParam],
      responseFields,
      responseExample: { data: doc.sample },
    },
    [doc.ids.update]: {
      ...common('update', 'PATCH', itemPath, 'packages:write'),
      pathParams: [itemIdParam],
      bodyParams: bodyParams(false),
      requestExample: { default_price: Math.round(doc.sample.default_price * 1.1) },
      responseExample: { data: { ...doc.sample, default_price: Math.round(doc.sample.default_price * 1.1) } },
    },
    [doc.ids.archive]: {
      ...common('archive', 'DELETE', itemPath, 'packages:write'),
      pathParams: [itemIdParam],
      responseExample: { data: { ...doc.sample, active: false } },
    },
  };
}

const TRIP_ADDON_FIELD = (name, type, key) => ({
  name, type, descriptionKey: `developerPortal.apiRef.fields.tripAddon.${key}`,
});

const TRIP_ADDON_RESPONSE_FIELDS = [
  TRIP_ADDON_FIELD('id', 'integer', 'id'),
  TRIP_ADDON_FIELD('source_type', 'string', 'sourceType'),
  TRIP_ADDON_FIELD('addon_id', 'integer|null', 'addonId'),
  TRIP_ADDON_FIELD('inventory_item_id', 'integer|null', 'inventoryItemId'),
  TRIP_ADDON_FIELD('mode', 'string', 'mode'),
  TRIP_ADDON_FIELD('name', 'string', 'name'),
  TRIP_ADDON_FIELD('unit_price', 'number', 'unitPrice'),
  TRIP_ADDON_FIELD('quantity', 'integer', 'quantity'),
  TRIP_ADDON_FIELD('amount', 'number', 'amount'),
  TRIP_ADDON_FIELD('free', 'boolean', 'free'),
  TRIP_ADDON_FIELD('day_number', 'integer|null', 'dayNumber'),
  TRIP_ADDON_FIELD('add_to_itinerary', 'boolean', 'addToItinerary'),
  TRIP_ADDON_FIELD('payment_link_url', 'string|null', 'paymentLinkUrl'),
  TRIP_ADDON_FIELD('payment_link_label', 'string|null', 'paymentLinkLabel'),
  TRIP_ADDON_FIELD('status', 'string', 'status'),
  TRIP_ADDON_FIELD('line_item_id', 'integer|null', 'lineItemId'),
  TRIP_ADDON_FIELD('itinerary_id', 'integer|null', 'itineraryId'),
];

const TRIP_ADDON_SAMPLE = {
  id: 41,
  trip_id: 1001,
  source_type: 'addon',
  addon_id: 21,
  inventory_item_id: null,
  mode: 'offer',
  name: 'Airport fast track',
  description: 'Meet and greet at the gate, fast-track security and passport control.',
  image_url: null,
  category: 'transfer',
  unit_price: 80,
  quantity: 1,
  amount: 80,
  currency: 'EUR',
  price_frequency: 'one_time',
  free: false,
  day_number: 1,
  add_to_itinerary: true,
  payment_link_url: 'https://buy.stripe.com/fast-track',
  payment_link_label: 'Pay with Stripe',
  status: 'available',
  line_item_id: null,
  itinerary_id: null,
  created_at: '2026-10-08T19:20:00.000Z',
};

const tripAddonExample = (key, body) => ({
  labelKey: `developerPortal.apiRef.tripAddonExamples.${key}`,
  body,
});

const TRIP_ADDON_EXAMPLES = [
  tripAddonExample('offerAddon', { addon_id: 21, day_number: 1 }),
  tripAddonExample('chargeInventory', { inventory_item_id: 14, mode: 'charge', quantity: 2, day_number: 4 }),
  tripAddonExample('complimentary', { addon_id: 22, mode: 'charge', free: true }),
  tripAddonExample('billOnly', { addon_id: 23, mode: 'charge', add_to_itinerary: false }),
  tripAddonExample('packageAddon', { package_item_id: 8 }),
];

export const API_SECTIONS = [
  {
    id: 'overview',
    titleKey: 'developerPortal.apiRef.sections.overview',
    endpoints: [
      'authentication',
      'making-requests',
      'versioning',
      'errors',
      'idempotency',
      'rate-limits',
    ],
  },
  {
    id: 'profile',
    titleKey: 'developerPortal.apiRef.sections.profile',
    endpoints: ['get-profile'],
  },
  {
    id: 'trips',
    titleKey: 'developerPortal.apiRef.sections.trips',
    endpoints: [
      'list-trips',
      'create-trip',
      'get-trip',
      'update-trip',
      'list-trip-itinerary',
      'create-trip-itinerary-item',
      'update-trip-itinerary-item',
      'delete-trip-itinerary-item',
      'list-member-itinerary',
      'create-member-itinerary-item',
      'update-member-itinerary-item',
      'delete-member-itinerary-item',
      'list-trip-documents',
      'upload-trip-document',
      'list-trip-members',
      'invite-trip-member',
      'list-trip-invitations',
      'list-trip-addons',
      'add-trip-addon',
      'remove-trip-addon',
      'archive-trip',
      'restore-trip',
    ],
  },
  {
    id: 'packages',
    titleKey: 'developerPortal.apiRef.sections.packages',
    endpoints: [
      'list-packages',
      'get-package',
      'create-package',
      'list-pricing-tiers',
      'create-pricing-tier',
      'update-pricing-tier',
      'delete-pricing-tier',
      'get-catalog',
      'custom-cards',
    ],
  },
  {
    id: 'dmcs',
    titleKey: 'developerPortal.apiRef.sections.dmcs',
    endpoints: [
      'list-dmcs',
      'get-dmc',
      'create-dmc',
      'update-dmc',
      'delete-dmc',
    ],
  },
  {
    id: 'inventory',
    titleKey: 'developerPortal.apiRef.sections.inventory',
    endpoints: Object.values(CATALOG_DOCS.inventory.ids),
  },
  {
    id: 'addons',
    titleKey: 'developerPortal.apiRef.sections.addons',
    endpoints: Object.values(CATALOG_DOCS.addon.ids),
  },
  {
    id: 'bookings',
    titleKey: 'developerPortal.apiRef.sections.bookings',
    endpoints: [
      'list-inquiries',
      'get-inquiry',
      'quote-inquiry',
      'list-bookings',
      'get-booking',
      'book-for-client',
      'create-checkout-link',
    ],
  },
  {
    id: 'clients',
    titleKey: 'developerPortal.apiRef.sections.clients',
    endpoints: ['list-clients', 'invite-client'],
  },
  {
    id: 'webhooks',
    titleKey: 'developerPortal.apiRef.sections.webhooks',
    endpoints: ['list-webhooks', 'create-webhook', 'update-webhook', 'delete-webhook', 'test-webhook'],
  },
];

/** @type {Record<string, object>} */
export const API_ENDPOINTS = {
  authentication: {
    id: 'authentication',
    kind: 'guide',
    titleKey: 'developerPortal.apiRef.endpoints.authentication.title',
    summaryKey: 'developerPortal.apiRef.endpoints.authentication.summary',
    bodyKey: 'developerPortal.apiRef.endpoints.authentication.body',
    guideSections: [
      {
        titleKey: 'developerPortal.apiRef.endpoints.authentication.sections.keys.title',
        bodyKey: 'developerPortal.apiRef.endpoints.authentication.sections.keys.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.authentication.sections.header.title',
        bodyKey: 'developerPortal.apiRef.endpoints.authentication.sections.header.body',
        code: 'Authorization: Bearer dvn_test_YOUR_KEY',
        codeLanguage: 'http',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.authentication.sections.example.title',
        bodyKey: 'developerPortal.apiRef.endpoints.authentication.sections.example.body',
        codeKey: 'authCurl',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.authentication.sections.scopes.title',
        bodyKey: 'developerPortal.apiRef.endpoints.authentication.sections.scopes.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.authentication.sections.security.title',
        bodyKey: 'developerPortal.apiRef.endpoints.authentication.sections.security.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.authentication.sections.js.title',
        bodyKey: 'developerPortal.apiRef.endpoints.authentication.sections.js.body',
        codeKey: 'authFetch',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.authentication.sections.response.title',
        bodyKey: 'developerPortal.apiRef.endpoints.authentication.sections.response.body',
        codeKey: 'authResponse',
      },
    ],
  },
  'making-requests': {
    id: 'making-requests',
    kind: 'guide',
    titleKey: 'developerPortal.apiRef.endpoints.makingRequests.title',
    summaryKey: 'developerPortal.apiRef.endpoints.makingRequests.summary',
    bodyKey: 'developerPortal.apiRef.endpoints.makingRequests.body',
    guideSections: [
      {
        titleKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.base.title',
        bodyKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.base.body',
        code: 'https://api.dejavunomad.com/v1',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.headers.title',
        bodyKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.headers.body',
        code: [
          'Authorization: Bearer dvn_test_YOUR_KEY',
          'Content-Type: application/json',
          'Accept: application/json',
          'Idempotency-Key: unique-operation-id   # POST / PUT / PATCH',
        ].join('\n'),
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.content.title',
        bodyKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.content.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.external.title',
        bodyKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.external.body',
        code: JSON.stringify({
          external_trip_id: 'crm-trip-441',
          external_customer_id: 'crm-client-88',
          title: 'Lisbon with the Millers',
        }, null, 2),
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.example.title',
        bodyKey: 'developerPortal.apiRef.endpoints.makingRequests.sections.example.body',
        codeKey: 'listPackagesCurl',
      },
    ],
  },
  versioning: {
    id: 'versioning',
    kind: 'guide',
    titleKey: 'developerPortal.apiRef.endpoints.versioning.title',
    summaryKey: 'developerPortal.apiRef.endpoints.versioning.summary',
    bodyKey: 'developerPortal.apiRef.endpoints.versioning.body',
    guideSections: [
      {
        titleKey: 'developerPortal.apiRef.endpoints.versioning.sections.url.title',
        bodyKey: 'developerPortal.apiRef.endpoints.versioning.sections.url.body',
        code: 'https://api.dejavunomad.com/v1/...',
        codeLanguage: 'text',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.versioning.sections.headers.title',
        bodyKey: 'developerPortal.apiRef.endpoints.versioning.sections.headers.body',
        code: 'X-API-Version: v1',
        codeLanguage: 'http',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.versioning.sections.compat.title',
        bodyKey: 'developerPortal.apiRef.endpoints.versioning.sections.compat.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.versioning.sections.breaking.title',
        bodyKey: 'developerPortal.apiRef.endpoints.versioning.sections.breaking.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.versioning.sections.deprecation.title',
        bodyKey: 'developerPortal.apiRef.endpoints.versioning.sections.deprecation.body',
      },
    ],
  },
  errors: {
    id: 'errors',
    kind: 'guide',
    titleKey: 'developerPortal.apiRef.endpoints.errors.title',
    summaryKey: 'developerPortal.apiRef.endpoints.errors.summary',
    bodyKey: 'developerPortal.apiRef.endpoints.errors.body',
    guideSections: [
      {
        titleKey: 'developerPortal.apiRef.endpoints.errors.sections.envelope.title',
        bodyKey: 'developerPortal.apiRef.endpoints.errors.sections.envelope.body',
        code: JSON.stringify({
          error: {
            code: 'validation_error',
            message: 'external_trip_id is required',
          },
        }, null, 2),
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.errors.sections.codes.title',
        bodyKey: 'developerPortal.apiRef.endpoints.errors.sections.codes.body',
        errorTable: true,
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.errors.sections.retry.title',
        bodyKey: 'developerPortal.apiRef.endpoints.errors.sections.retry.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.errors.sections.handle.title',
        bodyKey: 'developerPortal.apiRef.endpoints.errors.sections.handle.body',
        codeKey: 'errorHandleJs',
      },
    ],
    errorCodes: [
      { status: '401', code: 'unauthorized', descriptionKey: 'developerPortal.apiRef.errorCodes.unauthorized' },
      { status: '403', code: 'forbidden', descriptionKey: 'developerPortal.apiRef.errorCodes.forbidden' },
      { status: '403', code: 'partner_feature_limit', descriptionKey: 'developerPortal.apiRef.errorCodes.partnerFeatureLimit' },
      { status: '403', code: 'sandbox_write_blocked', descriptionKey: 'developerPortal.apiRef.errorCodes.sandboxWriteBlocked' },
      { status: '404', code: 'not_found', descriptionKey: 'developerPortal.apiRef.errorCodes.notFound' },
      { status: '409', code: 'conflict', descriptionKey: 'developerPortal.apiRef.errorCodes.conflict' },
      { status: '409', code: 'idempotency_key_conflict', descriptionKey: 'developerPortal.apiRef.errorCodes.idempotencyKeyConflict' },
      { status: '422', code: 'validation_error', descriptionKey: 'developerPortal.apiRef.errorCodes.validationError' },
      { status: '429', code: 'rate_limited', descriptionKey: 'developerPortal.apiRef.errorCodes.rateLimited' },
      { status: '429', code: 'sandbox_quota_exceeded', descriptionKey: 'developerPortal.apiRef.errorCodes.sandboxQuotaExceeded' },
      { status: '500', code: 'internal_error', descriptionKey: 'developerPortal.apiRef.errorCodes.internalError' },
    ],
  },
  idempotency: {
    id: 'idempotency',
    kind: 'guide',
    titleKey: 'developerPortal.apiRef.endpoints.idempotency.title',
    summaryKey: 'developerPortal.apiRef.endpoints.idempotency.summary',
    bodyKey: 'developerPortal.apiRef.endpoints.idempotency.body',
    guideSections: [
      {
        titleKey: 'developerPortal.apiRef.endpoints.idempotency.sections.when.title',
        bodyKey: 'developerPortal.apiRef.endpoints.idempotency.sections.when.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.idempotency.sections.header.title',
        bodyKey: 'developerPortal.apiRef.endpoints.idempotency.sections.header.body',
        code: 'Idempotency-Key: booking-create-9f3c2a1b',
        codeLanguage: 'http',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.idempotency.sections.behavior.title',
        bodyKey: 'developerPortal.apiRef.endpoints.idempotency.sections.behavior.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.idempotency.sections.example.title',
        bodyKey: 'developerPortal.apiRef.endpoints.idempotency.sections.example.body',
        codeKey: 'idempotencyCurl',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.idempotency.sections.tips.title',
        bodyKey: 'developerPortal.apiRef.endpoints.idempotency.sections.tips.body',
      },
    ],
  },
  'rate-limits': {
    id: 'rate-limits',
    kind: 'guide',
    titleKey: 'developerPortal.apiRef.endpoints.rateLimits.title',
    summaryKey: 'developerPortal.apiRef.endpoints.rateLimits.summary',
    bodyKey: 'developerPortal.apiRef.endpoints.rateLimits.body',
    guideSections: [
      {
        titleKey: 'developerPortal.apiRef.endpoints.rateLimits.sections.quotas.title',
        bodyKey: 'developerPortal.apiRef.endpoints.rateLimits.sections.quotas.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.rateLimits.sections.burst.title',
        bodyKey: 'developerPortal.apiRef.endpoints.rateLimits.sections.burst.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.rateLimits.sections.response.title',
        bodyKey: 'developerPortal.apiRef.endpoints.rateLimits.sections.response.body',
        code: JSON.stringify({
          error: {
            code: 'rate_limited',
            message: 'Too many requests. Retry after a short delay.',
          },
        }, null, 2),
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.rateLimits.sections.usage.title',
        bodyKey: 'developerPortal.apiRef.endpoints.rateLimits.sections.usage.body',
      },
    ],
  },
  'get-profile': {
    id: 'get-profile',
    method: 'GET',
    path: '/v1/profile',
    scope: 'account:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.getProfile.title',
    summaryKey: 'developerPortal.apiRef.endpoints.getProfile.summary',
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.profile.id' },
      { name: 'company_name', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.companyName' },
      { name: 'contact_name', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.contactName' },
      { name: 'email', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.email' },
      { name: 'phone_number', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.phoneNumber' },
      { name: 'website', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.website' },
      { name: 'logo_url', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.logoUrl' },
      { name: 'description', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.description' },
      { name: 'business_type', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.businessType' },
      { name: 'address', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.address' },
      { name: 'location', type: 'object', descriptionKey: 'developerPortal.apiRef.fields.profile.location' },
      { name: 'specialties', type: 'array', descriptionKey: 'developerPortal.apiRef.fields.profile.specialties' },
      { name: 'directory_slug', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.directorySlug' },
      { name: 'environment', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.profile.environment' },
    ],
    responseExample: {
      data: {
        id: 9,
        company_name: 'Coastal Voyages',
        contact_name: 'Sofia Romano',
        email: 'hello@coastalvoyages.com',
        phone_number: '+39 089 871 234',
        website: 'https://coastalvoyages.com',
        logo_url: 'https://cdn.dejavunomad.com/partners/9/logo.png',
        description: 'Boutique Amalfi Coast and Southern Italy itineraries.',
        business_type: 'travel_agency',
        business_type_other: null,
        address: 'Via Pietro Capuano 12, 84011 Amalfi SA, Italy',
        location: { city: 'Amalfi', region: 'Campania', country: 'IT' },
        specialties: ['honeymoons', 'food_and_wine', 'small_groups'],
        directory_slug: 'coastal-voyages',
        environment: 'test',
      },
    },
  },
  'list-trips': {
    id: 'list-trips',
    method: 'GET',
    path: '/v1/trips',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listTrips.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listTrips.summary',
    queryParams: [
      { name: 'external_trip_id', type: 'string', descriptionKey: 'developerPortal.apiRef.params.externalTripId' },
      { name: 'status', type: 'string', descriptionKey: 'developerPortal.apiRef.params.tripListStatus' },
    ],
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.trip.id' },
      { name: 'title', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.title' },
      { name: 'description', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.description' },
      { name: 'locations', type: 'array|null', descriptionKey: 'developerPortal.apiRef.fields.trip.locations' },
      { name: 'start_date', type: 'string (date)', descriptionKey: 'developerPortal.apiRef.fields.trip.startDate' },
      { name: 'end_date', type: 'string (date)', descriptionKey: 'developerPortal.apiRef.fields.trip.endDate' },
      { name: 'trip_type', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.tripType' },
      { name: 'image_url', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.imageUrl' },
      { name: 'trip_code', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.tripCode' },
      { name: 'partner_archived', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.trip.partnerArchived' },
      { name: 'external_trip_id', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.externalTripId' },
      { name: 'managed_by_partner', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.trip.managedByPartner' },
      { name: 'created_at', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.trip.createdAt' },
      { name: 'updated_at', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.trip.updatedAt' },
    ],
    responseNoteKey: 'developerPortal.apiRef.endpoints.listTrips.responseNote',
    responseExample: {
      data: [
        {
          id: 1000529,
          title: 'Reference Amalfi escape',
          description: 'Coastal villages, family dining, and a slow ferry day.',
          locations: [
            { name: 'Positano, Italy', coordinates: { lat: 40.628, lng: 14.4849 } },
            { name: 'Amalfi, Italy', coordinates: { lat: 40.634, lng: 14.6027 } },
          ],
          start_date: '2026-10-12T00:00:00.000Z',
          end_date: '2026-10-19T00:00:00.000Z',
          trip_type: 'family',
          image_url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200&q=80&auto=format',
          trip_code: null,
          partner_archived: false,
          external_trip_id: 'crm-trip-1001',
          managed_by_partner: true,
          created_at: '2026-08-29T03:23:09.000Z',
          updated_at: '2026-08-29T03:23:09.000Z',
        },
      ],
    },
  },
  'create-trip': {
    id: 'create-trip',
    method: 'POST',
    path: '/v1/trips',
    scope: 'trips:write',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.createTrip.title',
    summaryKey: 'developerPortal.apiRef.endpoints.createTrip.summary',
    bodyParams: [
      { name: 'title', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.tripTitle' },
      { name: 'client_email', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.clientEmail' },
      { name: 'start_date', type: 'date', required: true, descriptionKey: 'developerPortal.apiRef.params.startDate' },
      { name: 'end_date', type: 'date', required: true, descriptionKey: 'developerPortal.apiRef.params.endDate' },
      { name: 'description', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tripDescription' },
      { name: 'locations', type: 'array', required: false, descriptionKey: 'developerPortal.apiRef.params.tripLocations' },
      { name: 'trip_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tripType' },
      { name: 'image_url', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tripImageUrl' },
      { name: 'external_trip_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.externalTripId' },
      { name: 'external_customer_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.externalCustomerId' },
    ],
    requestExample: {
      title: 'Amalfi family trip',
      client_email: 'traveler@example.com',
      start_date: '2026-09-10',
      end_date: '2026-09-17',
      trip_type: 'family',
      image_url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200&q=80&auto=format',
      external_trip_id: 'crm-trip-1001',
    },
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.trip.id' },
      { name: 'title', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.title' },
      { name: 'description', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.description' },
      { name: 'locations', type: 'array|null', descriptionKey: 'developerPortal.apiRef.fields.trip.locations' },
      { name: 'start_date', type: 'string (date)', descriptionKey: 'developerPortal.apiRef.fields.trip.startDate' },
      { name: 'end_date', type: 'string (date)', descriptionKey: 'developerPortal.apiRef.fields.trip.endDate' },
      { name: 'trip_type', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.tripType' },
      { name: 'image_url', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.imageUrl' },
      { name: 'trip_code', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.tripCode' },
      { name: 'partner_archived', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.trip.partnerArchived' },
      { name: 'external_trip_id', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.externalTripId' },
      { name: 'managed_by_partner', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.trip.managedByPartner' },
      { name: 'created_at', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.trip.createdAt' },
      { name: 'updated_at', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.trip.updatedAt' },
    ],
    responseExample: {
      data: {
        id: 1001,
        title: 'Amalfi family trip',
        description: null,
        locations: null,
        start_date: '2026-09-10T00:00:00.000Z',
        end_date: '2026-09-17T00:00:00.000Z',
        trip_type: 'family',
        image_url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200&q=80&auto=format',
        trip_code: null,
        partner_archived: false,
        external_trip_id: 'crm-trip-1001',
        managed_by_partner: true,
        created_at: '2026-09-01T12:00:00.000Z',
        updated_at: '2026-09-01T12:00:00.000Z',
      },
    },
  },
  'get-trip': {
    id: 'get-trip',
    method: 'GET',
    path: '/v1/trips/{id}',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.getTrip.title',
    summaryKey: 'developerPortal.apiRef.endpoints.getTrip.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.trip.id' },
      { name: 'title', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.title' },
      { name: 'description', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.description' },
      { name: 'locations', type: 'array|null', descriptionKey: 'developerPortal.apiRef.fields.trip.locations' },
      { name: 'start_date', type: 'string (date)', descriptionKey: 'developerPortal.apiRef.fields.trip.startDate' },
      { name: 'end_date', type: 'string (date)', descriptionKey: 'developerPortal.apiRef.fields.trip.endDate' },
      { name: 'trip_type', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.tripType' },
      { name: 'image_url', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.imageUrl' },
      { name: 'trip_code', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.tripCode' },
      { name: 'partner_archived', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.trip.partnerArchived' },
      { name: 'external_trip_id', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.trip.externalTripId' },
      { name: 'managed_by_partner', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.trip.managedByPartner' },
      { name: 'created_at', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.trip.createdAt' },
      { name: 'updated_at', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.trip.updatedAt' },
      { name: 'members', type: 'array', descriptionKey: 'developerPortal.apiRef.fields.trip.members' },
      { name: 'members[].user_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.trip.memberUserId' },
      { name: 'members[].role', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.memberRole' },
      { name: 'members[].email', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.memberEmail' },
      { name: 'members[].username', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.trip.memberUsername' },
    ],
    responseNoteKey: 'developerPortal.apiRef.endpoints.getTrip.responseNote',
    responseExample: {
      data: {
        id: 1000529,
        title: 'Reference Amalfi escape',
        description: 'Coastal villages, family dining, and a slow ferry day.',
        locations: [
          { name: 'Positano, Italy', coordinates: { lat: 40.628, lng: 14.4849 } },
        ],
        start_date: '2026-10-12T00:00:00.000Z',
        end_date: '2026-10-19T00:00:00.000Z',
        trip_type: 'family',
        image_url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200&q=80&auto=format',
        trip_code: null,
        partner_archived: false,
        external_trip_id: 'crm-trip-1001',
        managed_by_partner: true,
        created_at: '2026-08-29T03:23:09.000Z',
        updated_at: '2026-08-29T03:23:09.000Z',
        members: [
          {
            user_id: 42,
            role: 'creator',
            email: 'traveler@example.com',
            username: 'traveler',
          },
        ],
      },
    },
  },
  'update-trip': {
    id: 'update-trip',
    method: 'PATCH',
    path: '/v1/trips/{id}',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.updateTrip.title',
    summaryKey: 'developerPortal.apiRef.endpoints.updateTrip.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    bodyParams: [
      { name: 'title', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tripTitle' },
      { name: 'description', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tripDescription' },
      { name: 'start_date', type: 'date', required: false, descriptionKey: 'developerPortal.apiRef.params.startDate' },
      { name: 'end_date', type: 'date', required: false, descriptionKey: 'developerPortal.apiRef.params.endDate' },
      { name: 'trip_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tripType' },
      { name: 'image_url', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tripImageUrl' },
      { name: 'external_trip_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.externalTripId' },
    ],
    requestExample: { title: 'Amalfi coast — updated' },
  },
  'list-trip-itinerary': {
    id: 'list-trip-itinerary',
    method: 'GET',
    path: '/v1/trips/{id}/itinerary',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listTripItinerary.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listTripItinerary.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.itinerary.id' },
      { name: 'trip_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.itinerary.tripId' },
      { name: 'name', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.itinerary.name' },
      { name: 'title', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.itinerary.title' },
      { name: 'location', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.itinerary.location' },
      { name: 'google_map_link', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.itinerary.googleMapLink' },
      { name: 'category', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.itinerary.category' },
      { name: 'transportation_type', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.itinerary.transportationType' },
      { name: 'transportation_data', type: 'object', descriptionKey: 'developerPortal.apiRef.fields.itinerary.transportationData' },
      { name: 'day_number', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.itinerary.dayNumber' },
      { name: 'start_datetime', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.itinerary.startDatetime' },
      { name: 'end_datetime', type: 'string (datetime)|null', descriptionKey: 'developerPortal.apiRef.fields.itinerary.endDatetime' },
      { name: 'description', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.itinerary.description' },
      { name: 'image_url', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.itinerary.imageUrl' },
      { name: 'cost', type: 'number|null', descriptionKey: 'developerPortal.apiRef.fields.itinerary.cost' },
      { name: 'cost_currency', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.itinerary.costCurrency' },
      { name: 'client_visible', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.itinerary.clientVisible' },
    ],
    responseExample: {
      data: [
        {
          id: 501,
          trip_id: 1001,
          name: 'Positano ferry',
          title: 'Positano ferry',
          location: 'Amalfi harbour',
          google_map_link: 'https://maps.google.com/?q=Amalfi+harbour',
          category: 'transport',
          transportation_type: 'ferry',
          transportation_data: {
            departure_port: 'Amalfi',
            arrival_port: 'Positano',
            departure_time: '2026-09-11T09:30',
            arrival_time: '2026-09-11T10:15',
            ferry_company: 'Travelmar',
            confirmation_number: 'FM-441',
          },
          day_number: 2,
          start_datetime: '2026-09-11 09:30:00',
          end_datetime: '2026-09-11 10:15:00',
          description: 'Morning ferry to Positano',
          image_url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200',
          cost: 28.5,
          cost_currency: 'EUR',
          client_visible: true,
        },
      ],
    },
  },
  'create-trip-itinerary-item': {
    id: 'create-trip-itinerary-item',
    method: 'POST',
    path: '/v1/trips/{id}/itinerary',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.createTripItineraryItem.title',
    summaryKey: 'developerPortal.apiRef.endpoints.createTripItineraryItem.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    bodyParams: [
      { name: 'title', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.itineraryTitle' },
      { name: 'name', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryName' },
      { name: 'location', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryLocation' },
      { name: 'google_map_link', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryGoogleMapLink' },
      { name: 'latitude', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryLatitude' },
      { name: 'longitude', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryLongitude' },
      { name: 'place_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryPlaceId' },
      { name: 'geo_source', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryGeoSource' },
      { name: 'category', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCategory' },
      { name: 'transportation_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryTransportationType' },
      { name: 'transportation_data', type: 'object', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryTransportationData' },
      { name: 'start_datetime', type: 'datetime', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryStartDatetime' },
      { name: 'end_datetime', type: 'datetime', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryEndDatetime' },
      { name: 'day_number', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryDayNumber' },
      { name: 'start_time', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryStartTime' },
      { name: 'description', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryDescription' },
      { name: 'notes', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryNotes' },
      { name: 'image_url', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryImageUrl' },
      { name: 'cost', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCost' },
      { name: 'cost_currency', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCostCurrency' },
      { name: 'client_visible', type: 'boolean', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryClientVisible' },
    ],
    requestExample: {
      title: 'Naples → Amalfi flight',
      location: 'NAP → QSR',
      google_map_link: 'https://maps.google.com/?q=Naples+Airport',
      latitude: 40.884,
      longitude: 14.2908,
      place_id: 'geoapify:naples-airport',
      geo_source: 'geoapify',
      category: 'transport',
      transportation_type: 'flight',
      transportation_data: {
        legs: [{
          airline: 'AZ',
          airline_code: 'AZ',
          flight_number: '1265',
          departure_airport: 'NAP',
          arrival_airport: 'LIN',
          departure_time: '2026-09-10T08:15',
          arrival_time: '2026-09-10T09:40',
        }],
        confirmation_number: 'ABC123',
      },
      description: 'Morning hop to Milan Linate',
      image_url: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200',
      cost: 186,
      cost_currency: 'EUR',
      client_visible: true,
    },
    examplesTitleKey: 'developerPortal.apiRef.itineraryExamples.title',
    examples: [
      {
        labelKey: 'developerPortal.apiRef.itineraryExamples.hotel',
        body: {
          title: 'Hotel Santa Caterina',
          location: 'Strada Amalfitana 9, Amalfi',
          google_map_link: 'https://maps.google.com/?q=Hotel+Santa+Caterina+Amalfi',
          category: 'hotel',
          start_datetime: '2026-09-10 15:00:00',
          end_datetime: '2026-09-13 11:00:00',
          description: 'Sea-view double, breakfast included. Confirmation HSC-88231.',
          image_url: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200',
          cost: 1260,
          cost_currency: 'EUR',
          client_visible: true,
        },
      },
      {
        labelKey: 'developerPortal.apiRef.itineraryExamples.restaurant',
        body: {
          title: 'Dinner at Da Gemma',
          location: 'Via Fra Gerardo Sasso 11, Amalfi',
          google_map_link: 'https://maps.google.com/?q=Da+Gemma+Amalfi',
          category: 'restaurant',
          start_datetime: '2026-09-10 20:00:00',
          end_datetime: '2026-09-10 22:00:00',
          description: 'Table for 4 on the terrace. Reservation under Miller.',
          image_url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200',
          cost: 240,
          cost_currency: 'EUR',
        },
      },
      {
        labelKey: 'developerPortal.apiRef.itineraryExamples.event',
        body: {
          title: 'Ravello Festival concert',
          location: 'Villa Rufolo, Ravello',
          google_map_link: 'https://maps.google.com/?q=Villa+Rufolo+Ravello',
          category: 'event',
          start_datetime: '2026-09-11 19:30:00',
          end_datetime: '2026-09-11 21:30:00',
          description: 'Sunset orchestra concert. Tickets in the Documents tab.',
          image_url: 'https://images.unsplash.com/photo-1465847899084-d164df4dedc6?w=1200',
          cost: 180,
          cost_currency: 'EUR',
        },
      },
      ...TRANSPORT_EXAMPLES,
      {
        labelKey: 'developerPortal.apiRef.itineraryExamples.activity',
        body: {
          title: 'Path of the Gods hike',
          location: 'Bomerano, Agerola',
          google_map_link: 'https://maps.google.com/?q=Sentiero+degli+Dei',
          category: 'activity',
          start_datetime: '2026-09-12 08:00:00',
          end_datetime: '2026-09-12 12:30:00',
          description: 'Guided 7 km hike ending in Nocelle. Bring water and good shoes.',
          image_url: 'https://images.unsplash.com/photo-1533105079780-92b9be482077?w=1200',
          cost: 95,
          cost_currency: 'EUR',
        },
      },
      {
        labelKey: 'developerPortal.apiRef.itineraryExamples.other',
        body: {
          title: 'Free afternoon',
          location: 'Positano',
          category: 'other',
          start_datetime: '2026-09-12 14:00:00',
          end_datetime: '2026-09-12 18:00:00',
          description: 'Beach time or shopping on Via dei Mulini — no bookings needed.',
        },
      },
    ],
  },
  'update-trip-itinerary-item': {
    id: 'update-trip-itinerary-item',
    method: 'PATCH',
    path: '/v1/trips/{id}/itinerary/{itemId}',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.updateTripItineraryItem.title',
    summaryKey: 'developerPortal.apiRef.endpoints.updateTripItineraryItem.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
      { name: 'itemId', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.itineraryItemId' },
    ],
    bodyParams: [
      { name: 'title', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryTitle' },
      { name: 'location', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryLocation' },
      { name: 'google_map_link', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryGoogleMapLink' },
      { name: 'latitude', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryLatitude' },
      { name: 'longitude', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryLongitude' },
      { name: 'place_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryPlaceId' },
      { name: 'geo_source', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryGeoSource' },
      { name: 'category', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCategory' },
      { name: 'transportation_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryTransportationType' },
      { name: 'transportation_data', type: 'object', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryTransportationData' },
      { name: 'start_datetime', type: 'datetime', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryStartDatetime' },
      { name: 'end_datetime', type: 'datetime', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryEndDatetime' },
      { name: 'day_number', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryDayNumber' },
      { name: 'start_time', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryStartTime' },
      { name: 'description', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryDescription' },
      { name: 'image_url', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryImageUrl' },
      { name: 'cost', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCost' },
      { name: 'cost_currency', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryCostCurrency' },
      { name: 'client_visible', type: 'boolean', required: false, descriptionKey: 'developerPortal.apiRef.params.itineraryClientVisible' },
    ],
    requestExample: {
      cost: 210,
      transportation_data: {
        legs: [{
          airline: 'AZ',
          flight_number: '1265',
          departure_airport: 'NAP',
          arrival_airport: 'LIN',
          departure_time: '2026-09-10T10:00',
          arrival_time: '2026-09-10T11:25',
        }],
        confirmation_number: 'ABC123',
      },
    },
  },
  'delete-trip-itinerary-item': {
    id: 'delete-trip-itinerary-item',
    method: 'DELETE',
    path: '/v1/trips/{id}/itinerary/{itemId}',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.deleteTripItineraryItem.title',
    summaryKey: 'developerPortal.apiRef.endpoints.deleteTripItineraryItem.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
      { name: 'itemId', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.itineraryItemId' },
    ],
  },
  'list-member-itinerary': {
    id: 'list-member-itinerary',
    method: 'GET',
    path: '/v1/trips/{id}/member-itinerary',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listMemberItinerary.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listMemberItinerary.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    queryParams: [
      { name: 'visibility', type: 'string', descriptionKey: 'developerPortal.apiRef.params.memberItineraryVisibilityFilter' },
    ],
    responseFields: MEMBER_ITINERARY_RESPONSE_FIELDS,
    responseExample: { data: [MEMBER_ITINERARY_SUBGROUP_RESPONSE] },
  },
  'create-member-itinerary-item': {
    id: 'create-member-itinerary-item',
    method: 'POST',
    path: '/v1/trips/{id}/member-itinerary',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.createMemberItineraryItem.title',
    summaryKey: 'developerPortal.apiRef.endpoints.createMemberItineraryItem.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    bodyParams: MEMBER_ITINERARY_BODY_PARAMS,
    requestExample: {
      title: 'Boat to Capri',
      location: 'Positano pier',
      category: 'transport',
      transportation_type: 'ferry',
      transportation_data: {
        departure_port: 'Positano',
        arrival_port: 'Capri',
        departure_time: '2026-09-13T09:00',
        arrival_time: '2026-09-13T09:45',
        ferry_company: 'NLG',
      },
      visibility: 'subgroup',
      subgroup_name: 'Capri crew',
      participants: ['user:43', 'invite:501'],
      cost: 60,
      cost_currency: 'EUR',
    },
    responseExample: { data: MEMBER_ITINERARY_SUBGROUP_RESPONSE },
    examplesTitleKey: 'developerPortal.apiRef.memberItineraryExamples.title',
    examples: [
      {
        labelKey: 'developerPortal.apiRef.memberItineraryExamples.personal',
        body: {
          title: 'Spa afternoon',
          location: 'Hotel Santa Caterina spa',
          category: 'activity',
          start_datetime: '2026-09-12 15:00:00',
          end_datetime: '2026-09-12 17:00:00',
          description: 'Couples massage, booked under Khan.',
          visibility: 'personal',
          cost: 240,
          cost_currency: 'EUR',
        },
      },
      {
        labelKey: 'developerPortal.apiRef.memberItineraryExamples.subgroup',
        body: {
          title: 'Cooking class',
          location: 'Mamma Agata, Ravello',
          category: 'event',
          start_datetime: '2026-09-14 10:00:00',
          end_datetime: '2026-09-14 14:00:00',
          visibility: 'subgroup',
          subgroup_name: 'Foodies',
          participants: ['user:42', 'invite:501'],
        },
      },
    ],
  },
  'update-member-itinerary-item': {
    id: 'update-member-itinerary-item',
    method: 'PATCH',
    path: '/v1/trips/{id}/member-itinerary/{itemId}',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.updateMemberItineraryItem.title',
    summaryKey: 'developerPortal.apiRef.endpoints.updateMemberItineraryItem.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
      { name: 'itemId', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.memberItineraryItemId' },
    ],
    bodyParams: MEMBER_ITINERARY_BODY_PARAMS.map((param) => ({ ...param, required: false })),
    requestExample: {
      subgroup_name: 'Capri crew + Lina',
      participants: ['user:43', 'invite:501', 'invite:502'],
    },
    responseExample: { data: MEMBER_ITINERARY_SUBGROUP_RESPONSE },
  },
  'delete-member-itinerary-item': {
    id: 'delete-member-itinerary-item',
    method: 'DELETE',
    path: '/v1/trips/{id}/member-itinerary/{itemId}',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.deleteMemberItineraryItem.title',
    summaryKey: 'developerPortal.apiRef.endpoints.deleteMemberItineraryItem.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
      { name: 'itemId', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.memberItineraryItemId' },
    ],
  },
  'list-trip-documents': {
    id: 'list-trip-documents',
    method: 'GET',
    path: '/v1/trips/{id}/documents',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listTripDocuments.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listTripDocuments.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    queryParams: [
      { name: 'audience', type: 'string', descriptionKey: 'developerPortal.apiRef.params.documentAudience' },
      { name: 'traveler_trip_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.params.travelerTripId' },
    ],
    responseExample: {
      data: [
        {
          id: 12,
          trip_id: 1001,
          category: 'ticket',
          title: 'Flight confirmation',
          audience: 'personal',
          visibility: 'private',
          for_traveler_trip_id: 1102,
          for_traveler_name: 'Aya Khan',
          download_url: '/v1/trips/1001/documents/12/download',
        },
      ],
      travelers: [
        { trip_id: 1102, label: 'Aya Khan', email: 'aya@example.com' },
      ],
      storage_available: true,
    },
  },
  'upload-trip-document': {
    id: 'upload-trip-document',
    method: 'POST',
    path: '/v1/trips/{id}/documents',
    scope: 'trips:write',
    sandbox: false,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.uploadTripDocument.title',
    summaryKey: 'developerPortal.apiRef.endpoints.uploadTripDocument.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    bodyParams: [
      { name: 'document', type: 'file', required: true, descriptionKey: 'developerPortal.apiRef.params.documentFile' },
      { name: 'title', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.documentTitle' },
      { name: 'category', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.documentCategory' },
      { name: 'audience', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.documentAudience' },
      { name: 'audience_trip_id', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.audienceTripId' },
      { name: 'subgroup_name', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.subgroupName' },
      { name: 'participant_trip_ids', type: 'array|string', required: false, descriptionKey: 'developerPortal.apiRef.params.participantTripIds' },
      { name: 'visibility', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.documentVisibility' },
      { name: 'notes', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.documentNotes' },
      { name: 'expires_on', type: 'date', required: false, descriptionKey: 'developerPortal.apiRef.params.documentExpiresOn' },
    ],
    requestExample: {
      audience: 'personal',
      audience_trip_id: 1102,
      category: 'ticket',
      title: 'Boarding pass',
    },
  },
  'download-trip-document': {
    id: 'download-trip-document',
    method: 'GET',
    path: '/v1/trips/{id}/documents/{documentId}/download',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.downloadTripDocument.title',
    summaryKey: 'developerPortal.apiRef.endpoints.downloadTripDocument.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
      { name: 'documentId', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.documentId' },
    ],
  },
  'download-trip-pdf': {
    id: 'download-trip-pdf',
    method: 'GET',
    path: '/v1/trips/{id}/pdf',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.downloadTripPdf.title',
    summaryKey: 'developerPortal.apiRef.endpoints.downloadTripPdf.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    queryParams: [
      { name: 'traveler_trip_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.params.travelerTripIdPdf' },
    ],
  },
  'list-trip-members': {
    id: 'list-trip-members',
    method: 'GET',
    path: '/v1/trips/{id}/members',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listTripMembers.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listTripMembers.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    responseExample: {
      data: [
        { user_id: 42, role: 'creator', email: 'traveler@example.com', username: 'traveler' },
      ],
    },
  },
  'invite-trip-member': {
    id: 'invite-trip-member',
    method: 'POST',
    path: '/v1/trips/{id}/invitations',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.inviteTripMember.title',
    summaryKey: 'developerPortal.apiRef.endpoints.inviteTripMember.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    bodyParams: [
      { name: 'email', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.inviteEmail' },
    ],
    requestExample: { email: 'lina.park@example.com' },
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.invitation.id' },
      { name: 'email', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.invitation.email' },
      { name: 'status', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.invitation.status' },
      { name: 'invited_user_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.invitation.invitedUserId' },
      { name: 'resent', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.invitation.resent' },
    ],
    responseExample: {
      data: {
        id: 501,
        trip_id: 1001,
        email: 'lina.park@example.com',
        status: 'pending',
        invited_user_id: null,
        resent: false,
      },
    },
  },
  'list-trip-invitations': {
    id: 'list-trip-invitations',
    method: 'GET',
    path: '/v1/trips/{id}/invitations',
    scope: 'trips:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listTripInvitations.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listTripInvitations.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    responseExample: {
      data: [
        {
          id: 501,
          trip_id: 1001,
          email: 'lina.park@example.com',
          status: 'pending',
          invited_user_id: null,
          email_sent_at: '2026-10-08 18:55:02',
          created_at: '2026-10-08 18:55:01',
          updated_at: '2026-10-08 18:55:02',
        },
      ],
    },
  },
  'list-trip-addons': {
    id: 'list-trip-addons',
    method: 'GET',
    path: '/v1/trips/{id}/addons',
    scope: 'bookings:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listTripAddons.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listTripAddons.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    responseFields: [
      ...TRIP_ADDON_RESPONSE_FIELDS,
      TRIP_ADDON_FIELD('line_items', 'array', 'lineItems'),
      TRIP_ADDON_FIELD('totals', 'object', 'totals'),
    ],
    responseExample: {
      data: [
        TRIP_ADDON_SAMPLE,
        {
          ...TRIP_ADDON_SAMPLE,
          id: 42,
          source_type: 'inventory',
          addon_id: null,
          inventory_item_id: 14,
          mode: 'charge',
          name: 'Riva Aquarama day charter (per day × 2)',
          category: 'yacht',
          unit_price: 1800,
          quantity: 2,
          amount: 3600,
          price_frequency: 'per_day',
          day_number: 4,
          status: 'unpaid',
          line_item_id: 703,
        },
      ],
      line_items: [
        { id: 703, booking_id: 12, name: 'Riva Aquarama day charter (per day × 2)', amount: '3600.00', currency: 'EUR', payment_status: 'unpaid', itinerary_id: null, package_item_id: null },
      ],
      totals: { unpaidTotal: 3600, paidTotal: 0, dueCount: 1, paidCount: 0 },
      booking_id: 12,
    },
  },
  'add-trip-addon': {
    id: 'add-trip-addon',
    method: 'POST',
    path: '/v1/trips/{id}/addons',
    scope: 'checkout:write',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.addTripAddon.title',
    summaryKey: 'developerPortal.apiRef.endpoints.addTripAddon.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    bodyParams: [
      { name: 'addon_id', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.tripAddonAddonId' },
      { name: 'inventory_item_id', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.tripAddonInventoryItemId' },
      { name: 'package_item_id', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.tripAddonPackageItemId' },
      { name: 'mode', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tripAddonMode' },
      { name: 'quantity', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.tripAddonQuantity' },
      { name: 'free', type: 'boolean', required: false, descriptionKey: 'developerPortal.apiRef.params.tripAddonFree' },
      { name: 'day_number', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.tripAddonDayNumber' },
      { name: 'add_to_itinerary', type: 'boolean', required: false, descriptionKey: 'developerPortal.apiRef.params.tripAddonAddToItinerary' },
    ],
    requestExample: { addon_id: 21, mode: 'offer', day_number: 1 },
    examplesTitleKey: 'developerPortal.apiRef.tripAddonExamples.title',
    examples: TRIP_ADDON_EXAMPLES,
    responseFields: TRIP_ADDON_RESPONSE_FIELDS,
    responseExample: { data: TRIP_ADDON_SAMPLE },
  },
  'remove-trip-addon': {
    id: 'remove-trip-addon',
    method: 'DELETE',
    path: '/v1/trips/{id}/addons/{offerId}',
    scope: 'checkout:write',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.removeTripAddon.title',
    summaryKey: 'developerPortal.apiRef.endpoints.removeTripAddon.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
      { name: 'offerId', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripAddonOfferId' },
    ],
    responseExample: { data: { id: 41, removed: true } },
  },
  ...catalogEndpoints('inventory'),
  ...catalogEndpoints('addon'),
  'archive-trip': {
    id: 'archive-trip',
    method: 'POST',
    path: '/v1/trips/{id}/archive',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.archiveTrip.title',
    summaryKey: 'developerPortal.apiRef.endpoints.archiveTrip.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    responseExample: {
      data: { id: 1001, partner_archived: true },
    },
  },
  'restore-trip': {
    id: 'restore-trip',
    method: 'POST',
    path: '/v1/trips/{id}/restore',
    scope: 'trips:write',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.restoreTrip.title',
    summaryKey: 'developerPortal.apiRef.endpoints.restoreTrip.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tripId' },
    ],
    responseExample: {
      data: { id: 1001, partner_archived: false },
    },
  },
  'list-packages': {
    id: 'list-packages',
    method: 'GET',
    path: '/v1/packages',
    scope: 'packages:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listPackages.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listPackages.summary',
    queryParams: [
      { name: 'external_package_id', type: 'string', descriptionKey: 'developerPortal.apiRef.params.externalPackageId' },
      { name: 'status', type: 'string', descriptionKey: 'developerPortal.apiRef.params.packageStatus' },
      { name: 'visibility', type: 'string', descriptionKey: 'developerPortal.apiRef.params.visibility' },
    ],
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.id' },
      { name: 'partner_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.partnerId' },
      { name: 'title', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.title' },
      { name: 'slug', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.slug' },
      { name: 'trip_code', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.tripCode' },
      { name: 'external_package_id', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.externalPackageId' },
      { name: 'description', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.description' },
      { name: 'short_description', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.shortDescription' },
      { name: 'cover_image', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.coverImage' },
      { name: 'gallery', type: 'array|null', descriptionKey: 'developerPortal.apiRef.fields.package.gallery' },
      { name: 'destination', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.destination' },
      { name: 'destination_country', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.destinationCountry' },
      { name: 'destination_coordinates', type: 'object|null', descriptionKey: 'developerPortal.apiRef.fields.package.destinationCoordinates' },
      { name: 'start_location', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.startLocation' },
      { name: 'end_location', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.endLocation' },
      { name: 'package_type', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.packageType' },
      { name: 'pricing_type', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.pricingType' },
      { name: 'duration_days', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.durationDays' },
      { name: 'duration_nights', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.durationNights' },
      { name: 'base_price', type: 'string (decimal)', descriptionKey: 'developerPortal.apiRef.fields.package.basePrice' },
      { name: 'currency', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.currency' },
      { name: 'original_price', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.originalPrice' },
      { name: 'discount_percent', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.discountPercent' },
      { name: 'discount_amount', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.discountAmount' },
      { name: 'discount_valid_until', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.discountValidUntil' },
      { name: 'is_featured', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.package.isFeatured' },
      { name: 'price_includes', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.priceIncludes' },
      { name: 'price_excludes', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.priceExcludes' },
      { name: 'available_from', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.availableFrom' },
      { name: 'available_until', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.availableUntil' },
      { name: 'max_travelers', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.maxTravelers' },
      { name: 'min_travelers', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.minTravelers' },
      { name: 'booking_deadline_days', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.bookingDeadlineDays' },
      { name: 'cancellation_policy', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.cancellationPolicy' },
      { name: 'is_customizable', type: 'boolean', descriptionKey: 'developerPortal.apiRef.fields.package.isCustomizable' },
      { name: 'customization_notes', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.customizationNotes' },
      { name: 'booking_method', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.bookingMethod' },
      { name: 'dmc_id', type: 'integer|null', descriptionKey: 'developerPortal.apiRef.fields.package.dmcId' },
      { name: 'dmc', type: 'object|null', descriptionKey: 'developerPortal.apiRef.fields.package.dmc' },
      { name: 'external_booking_url', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.externalBookingUrl' },
      { name: 'stripe_payment_url', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.stripePaymentUrl' },
      { name: 'status', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.status' },
      { name: 'visibility', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.visibility' },
      { name: 'rejection_reason', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.rejectionReason' },
      { name: 'published_at', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.publishedAt' },
      { name: 'meta_title', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.metaTitle' },
      { name: 'meta_description', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.metaDescription' },
      { name: 'tags', type: 'array|null', descriptionKey: 'developerPortal.apiRef.fields.package.tags' },
      { name: 'view_count', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.viewCount' },
      { name: 'inquiry_count', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.inquiryCount' },
      { name: 'booking_count', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.bookingCount' },
      { name: 'created_at', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.package.createdAt' },
      { name: 'updated_at', type: 'string (datetime)', descriptionKey: 'developerPortal.apiRef.fields.package.updatedAt' },
    ],
    responseNoteKey: 'developerPortal.apiRef.endpoints.listPackages.responseNote',
    responseExample: {
      data: [
        {
          id: 11,
          partner_id: 2,
          title: 'Reference Kyoto Heritage Walk',
          slug: 'reference-kyoto-heritage-walk',
          trip_code: null,
          external_package_id: 'crm-pkg-88',
          description: 'Temples, tea, and night markets.',
          short_description: 'Five days of temples, tea, and night markets.',
          cover_image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1600&q=80&auto=format',
          gallery: null,
          destination: 'Kyoto, Japan',
          destination_country: 'Japan',
          destination_coordinates: null,
          start_location: null,
          end_location: null,
          package_type: 'cultural',
          pricing_type: 'land_only',
          duration_days: 5,
          duration_nights: 4,
          base_price: '2890.00',
          currency: 'USD',
          original_price: null,
          discount_percent: null,
          discount_amount: null,
          discount_valid_until: null,
          is_featured: 0,
          price_includes: null,
          price_excludes: null,
          available_from: null,
          available_until: null,
          max_travelers: 20,
          min_travelers: 1,
          booking_deadline_days: 7,
          cancellation_policy: 'moderate',
          is_customizable: 1,
          customization_notes: null,
          booking_method: 'inquiry',
          dmc_id: 4,
          dmc: {
            id: 4,
            company_name: 'Indochina DMC',
            contact_name: 'Lan',
            email: 'lan@example.com',
            phone: '+8424123',
            notes: null,
          },
          external_booking_url: null,
          stripe_payment_url: null,
          status: 'published',
          visibility: 'marketplace',
          rejection_reason: null,
          published_at: '2026-08-29T03:23:10.000Z',
          meta_title: null,
          meta_description: null,
          tags: ['sandbox', 'docs'],
          view_count: 0,
          inquiry_count: 0,
          booking_count: 0,
          created_at: '2026-08-29T03:23:09.000Z',
          updated_at: '2026-08-29T03:23:10.000Z',
        },
      ],
    },
  },
  'get-package': {
    id: 'get-package',
    method: 'GET',
    path: '/v1/packages/{id}',
    scope: 'packages:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.getPackage.title',
    summaryKey: 'developerPortal.apiRef.endpoints.getPackage.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.packageId' },
    ],
    responseFields: [
      { name: '(all list fields)', type: '—', descriptionKey: 'developerPortal.apiRef.fields.package.allListFields' },
      { name: 'items', type: 'array', descriptionKey: 'developerPortal.apiRef.fields.package.items' },
      { name: 'items[].id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.itemId' },
      { name: 'items[].day_number', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.package.itemDayNumber' },
      { name: 'items[].name', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.itemName' },
      { name: 'items[].description', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.package.itemDescription' },
      { name: 'addons', type: 'array', descriptionKey: 'developerPortal.apiRef.fields.package.addons' },
      { name: 'addons[].additional_cost', type: 'number', descriptionKey: 'developerPortal.apiRef.fields.package.addonCost' },
      { name: 'addons[].currency', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.package.addonCurrency' },
      { name: 'pricing_tiers', type: 'array', descriptionKey: 'developerPortal.apiRef.fields.package.pricingTiers' },
      { name: 'dmc_id', type: 'integer|null', descriptionKey: 'developerPortal.apiRef.fields.package.dmcId' },
      { name: 'dmc', type: 'object|null', descriptionKey: 'developerPortal.apiRef.fields.package.dmc' },
    ],
    responseNoteKey: 'developerPortal.apiRef.endpoints.getPackage.responseNote',
    responseExample: {
      data: {
        id: 11,
        title: 'Reference Kyoto Heritage Walk',
        slug: 'reference-kyoto-heritage-walk',
        short_description: 'Five days of temples, tea, and night markets.',
        cover_image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1600&q=80&auto=format',
        destination: 'Kyoto, Japan',
        destination_country: 'Japan',
        package_type: 'cultural',
        duration_days: 5,
        duration_nights: 4,
        base_price: '2890.00',
        currency: 'USD',
        status: 'published',
        visibility: 'marketplace',
        items: [
          {
            id: 101,
            day_number: 1,
            name: 'Arrive Kansai + hotel check-in',
            description: 'Haruka Express orientation',
            is_included: 1,
            additional_cost: 0,
          },
        ],
        addons: [
          {
            id: 102,
            day_number: 0,
            name: 'Geiko evening',
            description: 'Private dinner performance',
            is_included: 0,
            additional_cost: 450,
            currency: 'USD',
          },
        ],
        pricing_tiers: [
          {
            id: 21,
            package_id: 11,
            tier_name: 'Single',
            description: 'Private room',
            price_per_person: '3290.00',
            occupancy_type: 'single',
            min_occupancy: 1,
            max_occupancy: 1,
            sort_order: 0,
          },
          {
            id: 22,
            package_id: 11,
            tier_name: 'Double',
            description: 'Shared double occupancy',
            price_per_person: '2890.00',
            occupancy_type: 'double',
            min_occupancy: 1,
            max_occupancy: 2,
            sort_order: 1,
          },
        ],
      },
    },
  },
  'create-package': {
    id: 'create-package',
    method: 'POST',
    path: '/v1/packages',
    scope: 'packages:write',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.createPackage.title',
    summaryKey: 'developerPortal.apiRef.endpoints.createPackage.summary',
    bodyParams: [
      { name: 'title', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.packageTitle' },
      { name: 'external_package_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.externalPackageId' },
      { name: 'visibility', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.visibility' },
      { name: 'short_description', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.packageShortDescription' },
      { name: 'description', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.packageDescription' },
      { name: 'destination', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.packageDestination' },
      { name: 'destination_country', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.destinationCountry' },
      { name: 'start_location', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.startLocation' },
      { name: 'end_location', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.endLocation' },
      { name: 'package_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.packageType' },
      { name: 'pricing_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.pricingType' },
      { name: 'duration_days', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.durationDays' },
      { name: 'duration_nights', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.durationNights' },
      { name: 'cover_image', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.packageCoverImage' },
      { name: 'gallery', type: 'array', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.gallery' },
      { name: 'base_price', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.packageBasePrice' },
      { name: 'currency', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.currency' },
      { name: 'price_includes', type: 'array|string', required: false, descriptionKey: 'developerPortal.apiRef.params.packagePriceIncludes' },
      { name: 'price_excludes', type: 'array|string', required: false, descriptionKey: 'developerPortal.apiRef.params.packagePriceExcludes' },
      { name: 'available_from', type: 'string (date)', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.availableFrom' },
      { name: 'available_until', type: 'string (date)', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.availableUntil' },
      { name: 'min_travelers', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.minTravelers' },
      { name: 'max_travelers', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.maxTravelers' },
      { name: 'booking_deadline_days', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.bookingDeadlineDays' },
      { name: 'cancellation_policy', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.packageCancellationPolicy' },
      { name: 'is_customizable', type: 'boolean', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.isCustomizable' },
      { name: 'customization_notes', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.customizationNotes' },
      { name: 'booking_method', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.bookingMethod' },
      { name: 'dmc_id', type: 'integer|null', required: false, descriptionKey: 'developerPortal.apiRef.params.packageDmcId' },
      { name: 'external_booking_url', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.externalBookingUrl' },
      { name: 'tags', type: 'array', required: false, descriptionKey: 'developerPortal.apiRef.fields.package.tags' },
      { name: 'pricing_tiers', type: 'array', required: false, descriptionKey: 'developerPortal.apiRef.params.packagePricingTiers' },
      { name: 'items', type: 'array', required: false, descriptionKey: 'developerPortal.apiRef.params.packageItems' },
      { name: 'addons', type: 'array', required: false, descriptionKey: 'developerPortal.apiRef.params.packageAddons' },
    ],
    requestExample: {
      title: 'Kyoto Heritage Walk',
      visibility: 'private_client',
      external_package_id: 'crm-pkg-88',
      short_description: 'Five days of temples and tea.',
      destination: 'Kyoto, Japan',
      base_price: 2890,
      currency: 'USD',
      price_includes: ['4 nights in a ryokan', 'Daily breakfast'],
      price_excludes: ['International flights'],
      available_from: '2026-10-01',
      available_until: '2027-03-31',
      cancellation_policy: 'moderate',
      max_travelers: 8,
      items: [
        { day_number: 1, name: 'Arrive Kansai + hotel check-in', category: 'transport', location: 'Kyoto' },
      ],
      pricing_tiers: [
        {
          tier_name: 'Single',
          price_per_person: 3290,
          occupancy_type: 'single',
          max_occupancy: 1,
        },
        {
          tier_name: 'Double',
          price_per_person: 2890,
          occupancy_type: 'double',
        },
      ],
      dmc_id: 4,
    },
  },
  'list-dmcs': {
    id: 'list-dmcs',
    method: 'GET',
    path: '/v1/dmcs',
    scope: 'packages:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listDmcs.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listDmcs.summary',
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.dmc.id' },
      { name: 'company_name', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.dmc.companyName' },
      { name: 'contact_name', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.dmc.contactName' },
      { name: 'email', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.dmc.email' },
      { name: 'phone', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.dmc.phone' },
      { name: 'notes', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.dmc.notes' },
    ],
    responseExample: {
      data: [
        {
          id: 4,
          company_name: 'Indochina DMC',
          contact_name: 'Lan',
          email: 'lan@example.com',
          phone: '+8424123',
          notes: 'USD nets',
        },
      ],
    },
  },
  'get-dmc': {
    id: 'get-dmc',
    method: 'GET',
    path: '/v1/dmcs/{id}',
    scope: 'packages:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.getDmc.title',
    summaryKey: 'developerPortal.apiRef.endpoints.getDmc.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.dmcId' },
    ],
    responseExample: {
      data: {
        id: 4,
        company_name: 'Indochina DMC',
        contact_name: 'Lan',
        email: 'lan@example.com',
        phone: '+8424123',
        notes: 'USD nets',
      },
    },
  },
  'create-dmc': {
    id: 'create-dmc',
    method: 'POST',
    path: '/v1/dmcs',
    scope: 'packages:write',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.createDmc.title',
    summaryKey: 'developerPortal.apiRef.endpoints.createDmc.summary',
    bodyParams: [
      { name: 'company_name', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.fields.dmc.companyName' },
      { name: 'contact_name', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.contactName' },
      { name: 'email', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.email' },
      { name: 'phone', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.phone' },
      { name: 'notes', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.notes' },
    ],
    requestExample: {
      company_name: 'Indochina DMC',
      contact_name: 'Lan',
      email: 'lan@example.com',
      phone: '+8424123',
      notes: 'USD nets',
    },
    responseExample: {
      data: {
        id: 4,
        company_name: 'Indochina DMC',
        contact_name: 'Lan',
        email: 'lan@example.com',
        phone: '+8424123',
        notes: 'USD nets',
      },
    },
  },
  'update-dmc': {
    id: 'update-dmc',
    method: 'PATCH',
    path: '/v1/dmcs/{id}',
    scope: 'packages:write',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.updateDmc.title',
    summaryKey: 'developerPortal.apiRef.endpoints.updateDmc.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.dmcId' },
    ],
    bodyParams: [
      { name: 'company_name', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.companyName' },
      { name: 'contact_name', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.contactName' },
      { name: 'email', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.email' },
      { name: 'phone', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.phone' },
      { name: 'notes', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.fields.dmc.notes' },
    ],
    requestExample: { phone: '+8424999' },
  },
  'delete-dmc': {
    id: 'delete-dmc',
    method: 'DELETE',
    path: '/v1/dmcs/{id}',
    scope: 'packages:write',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.deleteDmc.title',
    summaryKey: 'developerPortal.apiRef.endpoints.deleteDmc.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.dmcId' },
    ],
    responseExample: { data: { id: 4, deleted: true } },
  },
  'list-inquiries': {
    id: 'list-inquiries',
    method: 'GET',
    path: '/v1/inquiries',
    scope: 'bookings:read',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.listInquiries.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listInquiries.summary',
    queryParams: [
      { name: 'status', type: 'string', descriptionKey: 'developerPortal.apiRef.params.inquiryStatus' },
      { name: 'package_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.params.packageId' },
      { name: 'dmc_id', type: 'integer', descriptionKey: 'developerPortal.apiRef.params.dmcId' },
      { name: 'archived', type: 'string', descriptionKey: 'developerPortal.apiRef.params.inquiryArchived' },
    ],
    responseFields: [
      { name: 'id', type: 'integer', descriptionKey: 'developerPortal.apiRef.fields.inquiry.id' },
      { name: 'package_id', type: 'integer|null', descriptionKey: 'developerPortal.apiRef.fields.inquiry.packageId' },
      { name: 'package_title', type: 'string|null', descriptionKey: 'developerPortal.apiRef.fields.inquiry.packageTitle' },
      { name: 'name', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.inquiry.name' },
      { name: 'email', type: 'string', descriptionKey: 'developerPortal.apiRef.fields.inquiry.email' },
      { name: 'dmc_id', type: 'integer|null', descriptionKey: 'developerPortal.apiRef.fields.inquiry.dmcId' },
      { name: 'dmc', type: 'object|null', descriptionKey: 'developerPortal.apiRef.fields.inquiry.dmc' },
      { name: 'dmc_net_amount', type: 'number|null', descriptionKey: 'developerPortal.apiRef.fields.inquiry.dmcNet' },
      { name: 'quoted_price', type: 'number|null', descriptionKey: 'developerPortal.apiRef.fields.inquiry.quotedPrice' },
      { name: 'margin', type: 'number|null', descriptionKey: 'developerPortal.apiRef.fields.inquiry.margin' },
    ],
    responseExample: {
      data: [
        {
          id: 18,
          status: 'new',
          package_id: 11,
          package_title: 'Reference Kyoto Heritage Walk',
          name: 'Aya Kamboh',
          email: 'aya@example.com',
          dmc_id: 4,
          dmc_net_amount: 900,
          quoted_price: 1200,
          margin: 300,
          dmc: {
            id: 4,
            company_name: 'Indochina DMC',
            contact_name: 'Lan',
            email: 'lan@example.com',
            phone: '+8424123',
            notes: null,
          },
        },
      ],
    },
  },
  'get-inquiry': {
    id: 'get-inquiry',
    method: 'GET',
    path: '/v1/inquiries/{id}',
    scope: 'bookings:read',
    sandbox: false,
    titleKey: 'developerPortal.apiRef.endpoints.getInquiry.title',
    summaryKey: 'developerPortal.apiRef.endpoints.getInquiry.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.inquiryId' },
    ],
    responseExample: {
      data: {
        id: 18,
        status: 'new',
        package_id: 11,
        dmc_id: 4,
        dmc_net_amount: 900,
        quoted_price: 1200,
        margin: 300,
      },
    },
  },
  'quote-inquiry': {
    id: 'quote-inquiry',
    method: 'PATCH',
    path: '/v1/inquiries/{id}',
    scope: 'packages:write',
    sandbox: false,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.quoteInquiry.title',
    summaryKey: 'developerPortal.apiRef.endpoints.quoteInquiry.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.inquiryId' },
    ],
    bodyParams: [
      { name: 'dmc_net_amount', type: 'number|null', required: false, descriptionKey: 'developerPortal.apiRef.fields.inquiry.dmcNet' },
      { name: 'quoted_price', type: 'number|null', required: false, descriptionKey: 'developerPortal.apiRef.fields.inquiry.quotedPrice' },
    ],
    requestExample: { dmc_net_amount: 900, quoted_price: 1200 },
    responseExample: {
      data: {
        id: 18,
        dmc_net_amount: 900,
        quoted_price: 1200,
        margin: 300,
      },
    },
  },
  'list-pricing-tiers': {
    id: 'list-pricing-tiers',
    method: 'GET',
    path: '/v1/packages/{id}/pricing-tiers',
    scope: 'packages:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listPricingTiers.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listPricingTiers.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.packageId' },
    ],
    responseExample: {
      data: [
        {
          id: 21,
          package_id: 11,
          tier_name: 'Single',
          price_per_person: '3290.00',
          occupancy_type: 'single',
          min_occupancy: 1,
          max_occupancy: 1,
        },
      ],
    },
  },
  'create-pricing-tier': {
    id: 'create-pricing-tier',
    method: 'POST',
    path: '/v1/packages/{id}/pricing-tiers',
    scope: 'packages:write',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.createPricingTier.title',
    summaryKey: 'developerPortal.apiRef.endpoints.createPricingTier.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.packageId' },
    ],
    bodyParams: [
      { name: 'tier_name', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.tierName' },
      { name: 'price_per_person', type: 'number', required: true, descriptionKey: 'developerPortal.apiRef.params.tierPrice' },
      { name: 'occupancy_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tierOccupancy' },
      { name: 'min_occupancy', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.tierMinOccupancy' },
      { name: 'max_occupancy', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.tierMaxOccupancy' },
      { name: 'description', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tierDescription' },
      { name: 'stripe_payment_url', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tierStripeUrl' },
    ],
    requestExample: {
      tier_name: 'Triple',
      price_per_person: 2590,
      occupancy_type: 'triple',
      min_occupancy: 1,
      max_occupancy: 3,
    },
  },
  'update-pricing-tier': {
    id: 'update-pricing-tier',
    method: 'PATCH',
    path: '/v1/packages/{id}/pricing-tiers/{tierId}',
    scope: 'packages:write',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.updatePricingTier.title',
    summaryKey: 'developerPortal.apiRef.endpoints.updatePricingTier.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.packageId' },
      { name: 'tierId', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tierId' },
    ],
    bodyParams: [
      { name: 'tier_name', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tierName' },
      { name: 'price_per_person', type: 'number', required: false, descriptionKey: 'developerPortal.apiRef.params.tierPrice' },
      { name: 'occupancy_type', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.tierOccupancy' },
      { name: 'is_available', type: 'boolean', required: false, descriptionKey: 'developerPortal.apiRef.params.tierAvailable' },
    ],
    requestExample: {
      price_per_person: 2690,
      is_available: true,
    },
  },
  'delete-pricing-tier': {
    id: 'delete-pricing-tier',
    method: 'DELETE',
    path: '/v1/packages/{id}/pricing-tiers/{tierId}',
    scope: 'packages:write',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.deletePricingTier.title',
    summaryKey: 'developerPortal.apiRef.endpoints.deletePricingTier.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.packageId' },
      { name: 'tierId', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.tierId' },
    ],
  },
  'get-catalog': {
    id: 'get-catalog',
    method: 'GET',
    path: '/v1/catalog',
    scope: 'catalog:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.getCatalog.title',
    summaryKey: 'developerPortal.apiRef.endpoints.getCatalog.summary',
  },
  'custom-cards': {
    id: 'custom-cards',
    kind: 'guide',
    titleKey: 'developerPortal.apiRef.endpoints.customCards.title',
    summaryKey: 'developerPortal.apiRef.endpoints.customCards.summary',
    bodyKey: 'developerPortal.apiRef.endpoints.customCards.body',
    guideSections: [
      {
        titleKey: 'developerPortal.apiRef.endpoints.customCards.sections.packages.title',
        bodyKey: 'developerPortal.apiRef.endpoints.customCards.sections.packages.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.customCards.sections.trips.title',
        bodyKey: 'developerPortal.apiRef.endpoints.customCards.sections.trips.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.customCards.sections.links.title',
        bodyKey: 'developerPortal.apiRef.endpoints.customCards.sections.links.body',
      },
      {
        titleKey: 'developerPortal.apiRef.endpoints.customCards.sections.widget.title',
        bodyKey: 'developerPortal.apiRef.endpoints.customCards.sections.widget.body',
      },
    ],
  },
  'list-bookings': {
    id: 'list-bookings',
    method: 'GET',
    path: '/v1/bookings',
    scope: 'bookings:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listBookings.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listBookings.summary',
    queryParams: [
      { name: 'external_booking_id', type: 'string', descriptionKey: 'developerPortal.apiRef.params.externalBookingId' },
      { name: 'status', type: 'string', descriptionKey: 'developerPortal.apiRef.params.bookingStatus' },
    ],
  },
  'get-booking': {
    id: 'get-booking',
    method: 'GET',
    path: '/v1/bookings/{ref}',
    scope: 'bookings:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.getBooking.title',
    summaryKey: 'developerPortal.apiRef.endpoints.getBooking.summary',
    pathParams: [
      { name: 'ref', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.bookingRef' },
    ],
  },
  'book-for-client': {
    id: 'book-for-client',
    method: 'POST',
    path: '/v1/bookings/book-for-client',
    scope: 'checkout:write',
    sandbox: false,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.bookForClient.title',
    summaryKey: 'developerPortal.apiRef.endpoints.bookForClient.summary',
    bodyParams: [
      { name: 'package_id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.packageId' },
      { name: 'client_email', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.clientEmail' },
      { name: 'travelers', type: 'integer', required: false, descriptionKey: 'developerPortal.apiRef.params.travelers' },
      { name: 'external_booking_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.externalBookingId' },
      { name: 'external_reservation_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.externalReservationId' },
    ],
    requestExample: {
      package_id: 123,
      client_email: 'traveler@example.com',
      travelers: 2,
      external_booking_id: 'crm-booking-56',
    },
  },
  'create-checkout-link': {
    id: 'create-checkout-link',
    method: 'POST',
    path: '/v1/checkout-links',
    scope: 'checkout:write',
    sandbox: false,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.createCheckoutLink.title',
    summaryKey: 'developerPortal.apiRef.endpoints.createCheckoutLink.summary',
    bodyParams: [
      { name: 'package_id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.packageId' },
      { name: 'client_email', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.clientEmail' },
      { name: 'external_booking_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.externalBookingId' },
    ],
    requestExample: {
      package_id: 123,
      client_email: 'traveler@example.com',
      external_booking_id: 'crm-booking-55',
    },
    responseExample: {
      data: {
        url: 'https://dejavunomad.com/packages/kyoto-heritage?client=eyJ...',
        package_id: 123,
        expires_in_days: 14,
      },
    },
  },
  'list-clients': {
    id: 'list-clients',
    method: 'GET',
    path: '/v1/clients',
    scope: 'clients:read',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listClients.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listClients.summary',
    queryParams: [
      { name: 'external_customer_id', type: 'string', descriptionKey: 'developerPortal.apiRef.params.externalCustomerId' },
    ],
  },
  'invite-client': {
    id: 'invite-client',
    method: 'POST',
    path: '/v1/clients/invite',
    scope: 'clients:write',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.inviteClient.title',
    summaryKey: 'developerPortal.apiRef.endpoints.inviteClient.summary',
    bodyParams: [
      { name: 'email', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.clientEmail' },
      { name: 'external_customer_id', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.externalCustomerId' },
    ],
    requestExample: {
      email: 'traveler@example.com',
      external_customer_id: 'crm-cust-9',
    },
  },
  'list-webhooks': {
    id: 'list-webhooks',
    method: 'GET',
    path: '/v1/webhooks',
    scope: 'webhooks:manage',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.listWebhooks.title',
    summaryKey: 'developerPortal.apiRef.endpoints.listWebhooks.summary',
  },
  'create-webhook': {
    id: 'create-webhook',
    method: 'POST',
    path: '/v1/webhooks',
    scope: 'webhooks:manage',
    sandbox: true,
    idempotent: true,
    titleKey: 'developerPortal.apiRef.endpoints.createWebhook.title',
    summaryKey: 'developerPortal.apiRef.endpoints.createWebhook.summary',
    bodyParams: [
      { name: 'url', type: 'string', required: true, descriptionKey: 'developerPortal.apiRef.params.webhookUrl' },
      { name: 'events', type: 'string[]', required: false, descriptionKey: 'developerPortal.apiRef.params.webhookEvents' },
      { name: 'environment', type: 'string', required: false, descriptionKey: 'developerPortal.apiRef.params.webhookEnv' },
    ],
    requestExample: {
      url: 'https://example.com/hooks/dvn',
      events: ['booking.created', 'trip.updated'],
      environment: 'test',
    },
  },
  'update-webhook': {
    id: 'update-webhook',
    method: 'PATCH',
    path: '/v1/webhooks/{id}',
    scope: 'webhooks:manage',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.updateWebhook.title',
    summaryKey: 'developerPortal.apiRef.endpoints.updateWebhook.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.webhookId' },
    ],
  },
  'delete-webhook': {
    id: 'delete-webhook',
    method: 'DELETE',
    path: '/v1/webhooks/{id}',
    scope: 'webhooks:manage',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.deleteWebhook.title',
    summaryKey: 'developerPortal.apiRef.endpoints.deleteWebhook.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.webhookId' },
    ],
  },
  'test-webhook': {
    id: 'test-webhook',
    method: 'POST',
    path: '/v1/webhooks/{id}/test',
    scope: 'webhooks:manage',
    sandbox: true,
    titleKey: 'developerPortal.apiRef.endpoints.testWebhook.title',
    summaryKey: 'developerPortal.apiRef.endpoints.testWebhook.summary',
    pathParams: [
      { name: 'id', type: 'integer', required: true, descriptionKey: 'developerPortal.apiRef.params.webhookId' },
    ],
  },
};

export function findEndpoint(id) {
  return API_ENDPOINTS[id] || null;
}

export function sectionForEndpoint(endpointId) {
  return API_SECTIONS.find((s) => s.endpoints.includes(endpointId)) || null;
}
